import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import md5 from 'md5';
import { Subject, Subscription } from 'rxjs';
import { ConnectionService } from '../../../services/connection.service';
import { LocalStorageService } from '../../../services/local-storage.service';
import { RoutingService } from '../../../services/routing.service';
import { noOnlyWhitespaceValidator, PATTERN_TEXT } from '../../../utils/normalize-string';
import { LanguageComponent } from '../../language/language.component';
import { AlertComponent } from '../../shared/card/alert/alert.component';

// This is an invalid password hash, used to reset password
const INVALID_PASSWORD_HASH = '4f0cc03bba4a7985a1311cbe02ccd763';

@Component({
  selector: 'forget-password',
  templateUrl: './forget-password.component.html',
  styleUrls: ['./forget-password.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    LanguageComponent,
    RouterModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    TranslatePipe,
    AlertComponent,
    MatInputModule,
    MatButtonModule,
  ],
})
export class ForgetPasswordComponent implements OnInit {
  public email: FormControl = new FormControl();
  public isDisplayLoader = false;
  private resetSubscription: Subscription;
  public isResetPassword: boolean;
  private componentDestroy$ = new Subject<void>();
  public isExpiredToken = false;
  public resultOnSendMail: string;
  public resultOnResetPassword: 'SUCCESS_PASSWORD' | 'INVALID_PASSWORD' | 'ERROR_RESET_PASSWORD' | 'ERROR';
  public currentToken: string;
  public hide: boolean = true;
  public hideConfirm: boolean = true;
  public $isMobile = this._routingService.$isMobile;

  public passwordForm: FormGroup = this.fb.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
      passwordConfirm: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
    },
    {
      validators: [this.passwordValidation],
    }
  );

  get password() {
    return this.passwordForm?.get('password');
  }
  get passwordConfirm() {
    return this.passwordForm.get('passwordConfirm');
  }

  public dictionnaryErrors = {
    SUCCESS: true,
    USER_NOT_FOUND: true,
    TO_MANY_REQUEST: true,
    SUCCESS_PASSWORD: true,
    INVALID_PASSWORD: true,
    ERROR_RESET_PASSWORD: true,
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private connectionService: ConnectionService,
    private _routingService: RoutingService
  ) {}

  ngOnInit(): void {
    this.resetSubscription = this.route.queryParamMap.subscribe(params => {
      if (params.get('token')) {
        this.isResetPassword = true;
        this.currentToken = params.get('token');
        this.email.setValue(undefined);
        this.password.setValue(undefined);
        this.passwordConfirm.setValue(undefined);
      } else {
        this.initMailInput();
        this.isResetPassword = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.componentDestroy$.next();
    this.componentDestroy$.complete();
    if (this.resetSubscription) this.resetSubscription.unsubscribe();
  }

  private initMailInput(): void {
    this.email = new FormControl('', [Validators.required, Validators.email]);
  }

  public async send(): Promise<void> {
    if (this.email.value && this.email.valid) {
      this.isExpiredToken = false;
      this.isDisplayLoader = true;
      this.resultOnSendMail = await this.connectionService.sendMailForResetPassword(this.email.value);
      this.isDisplayLoader = false;
    }
  }

  public async reset(): Promise<void> {
    if (this.passwordForm.valid) {
      this.isDisplayLoader = true;
      this.resultOnResetPassword = await this.connectionService.resetPassword(
        this.passwordConfirm.value,
        this.currentToken
      );

      switch (this.resultOnResetPassword) {
        case 'SUCCESS_PASSWORD':
          setTimeout(() => {
            this.router.navigate(['/login']);
            LocalStorageService.removeElement('accountConnect');
          }, 3000);
          break;
        case 'INVALID_PASSWORD':
          this.isDisplayLoader = false;
          break;
        case 'ERROR_RESET_PASSWORD':
          this.isDisplayLoader = false;
          setTimeout(() => {
            this.isExpiredToken = true;
            this.isResetPassword = false;
            this.initMailInput();
            this.resultOnResetPassword = undefined;
          }, 4000);
          break;
        default:
          this.isDisplayLoader = false;
          this.resultOnResetPassword = 'ERROR';
          break;
      }
    }
  }

  private passwordValidation(formGroup: FormGroup): Object | null {
    const password = formGroup.get('password')?.value;
    const passwordConfirm = formGroup.get('passwordConfirm')?.value;

    if (password !== passwordConfirm) return { passError: true };
    else if (password && md5(password) === INVALID_PASSWORD_HASH) return { passInvalid: true };

    return null;
  }
}
